import React from 'react';
import { Field, inputClass } from './BackgroundFields';
import ColorFillField from './ColorFillField';
import IconPickerField from './IconPickerField';
import ShapeFields from './ShapeFields';
import ButtonTypographyFields from './ButtonTypographyFields';
import HoverEffectFields from './HoverEffectFields';
import WebCreatorTextEditor from './WebCreatorTextEditor';
import { resolveIconStrokeColor } from '../utils/colorFillUtils';
import { plainTextToEditorHtml } from '../utils/cardRichText';
import '../styles/webCreatorRichText.css';

function readImageFileToProp(setProp, propKey, e) {
  const file = e.target.files?.[0];
  e.target.value = '';
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => setProp(propKey, reader.result);
  reader.readAsDataURL(file);
}

function CardImageSourceField({
  label,
  urlKey,
  altKey,
  props,
  setProp,
  showAlt = false,
  previewClassName = 'max-h-40',
}) {
  const url = props[urlKey] || '';

  return (
    <div className="space-y-3">
      <Field label={label}>
        <input
          className={inputClass}
          value={url}
          onChange={(e) => setProp(urlKey, e.target.value)}
          placeholder="https://… ou importer un fichier ci-dessous"
        />
      </Field>
      <Field label="Importer une image">
        <input
          type="file"
          accept="image/*,.svg"
          className="text-sm w-full"
          onChange={(e) => readImageFileToProp(setProp, urlKey, e)}
        />
      </Field>
      {showAlt ? (
        <Field label="Texte alternatif">
          <input
            className={inputClass}
            value={props[altKey] || ''}
            onChange={(e) => setProp(altKey, e.target.value)}
          />
        </Field>
      ) : null}
      {url ? (
        <div className="rounded-lg border border-border overflow-hidden bg-muted/30 p-3 flex justify-center">
          <img src={url} alt="" className={`max-w-full object-contain ${previewClassName}`} />
        </div>
      ) : null}
    </div>
  );
}

function CardHtmlEditorField({ label, valueKey, formatKey, props, setProp, siteTypography }) {
  const html = plainTextToEditorHtml(props[valueKey]);

  return (
    <div className="space-y-2 rounded-lg border border-border p-3 bg-muted/5">
      <p className="text-xs font-semibold text-foreground">{label}</p>
      <WebCreatorTextEditor
        value={html}
        onChange={(nextHtml) => {
          setProp(valueKey, nextHtml);
          setProp(formatKey, 'html');
        }}
        inheritSiteFont
        siteTypography={siteTypography}
      />
    </div>
  );
}

function CardLinkFields({ props, setProp, prefix = 'link', sections = [] }) {
  const linkTypeKey = prefix === 'button' ? 'buttonLinkType' : 'linkType';
  const anchorKey = prefix === 'button' ? 'buttonSectionAnchor' : 'sectionAnchor';
  const urlKey = prefix === 'button' ? 'buttonUrl' : 'url';
  const pathKey = prefix === 'button' ? 'buttonPublicPath' : 'publicPath';
  const linkType = props[linkTypeKey] || 'section';
  const anchorValue = String(props[anchorKey] || '').replace(/^#/, '');

  return (
    <>
      <Field label="Type de lien">
        <select
          className={inputClass}
          value={linkType}
          onChange={(e) => setProp(linkTypeKey, e.target.value)}
        >
          <option value="section">Ancre — section de la page</option>
          <option value="publicPath">Page publique</option>
          <option value="url">URL externe</option>
        </select>
      </Field>
      {linkType === 'section' ? (
        <>
          {sections.length > 0 ? (
            <Field label="Section cible">
              <select
                className={inputClass}
                value={anchorValue}
                onChange={(e) => setProp(anchorKey, e.target.value)}
              >
                <option value="">— Choisir une section —</option>
                {sections.map((s) => (
                  <option key={s.id} value={s.anchor}>
                    {s.label} (#{s.anchor})
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          <Field label="Ancre (identifiant de section)">
            <input
              className={inputClass}
              value={props[anchorKey] || ''}
              onChange={(e) =>
                setProp(anchorKey, String(e.target.value || '').replace(/^#/, ''))
              }
              placeholder="accueil"
            />
          </Field>
          <p className="text-[11px] text-muted-foreground leading-relaxed -mt-1">
            Correspond à l&apos;ancre définie dans les paramètres de chaque section (sans #).
          </p>
        </>
      ) : null}
      {linkType === 'publicPath' ? (
        <Field label="Chemin public">
          <input
            className={inputClass}
            value={props[pathKey] || ''}
            onChange={(e) => setProp(pathKey, e.target.value)}
            placeholder="/commande"
          />
        </Field>
      ) : null}
      {linkType === 'url' ? (
        <Field label="URL">
          <input
            className={inputClass}
            value={props[urlKey] || ''}
            onChange={(e) => setProp(urlKey, e.target.value)}
            placeholder="https://"
          />
        </Field>
      ) : null}
    </>
  );
}

function MediaCornerFields({ side, props, setProp }) {
  const prefix = side === 'left' ? 'left' : 'right';
  const mediaType = props[`${prefix}MediaType`] || (side === 'left' ? 'icon' : 'none');

  return (
    <div className="rounded-lg border border-border p-3 space-y-3 bg-muted/10">
      <p className="text-xs font-semibold text-foreground">
        {side === 'left' ? 'Visuel en haut à gauche' : 'Visuel en haut à droite'}
      </p>
      <Field label="Type">
        <select
          className={inputClass}
          value={mediaType}
          onChange={(e) => setProp(`${prefix}MediaType`, e.target.value)}
        >
          <option value="none">Aucun</option>
          <option value="icon">Icône</option>
          <option value="image">Image / SVG (fichier ou URL)</option>
        </select>
      </Field>
      {mediaType === 'icon' ? (
        <>
          <Field label="Icône">
            <IconPickerField
              value={props[`${prefix}IconName`]}
              onChange={(v) => setProp(`${prefix}IconName`, v)}
              previewColor={resolveIconStrokeColor(props[`${prefix}IconColor`], '#2563eb')}
              previewSize={props[`${prefix}IconSize`] ?? 28}
            />
          </Field>
          <Field label="Couleur">
            <input
              type="color"
              className="h-9 w-full rounded border border-border"
              value={resolveIconStrokeColor(props[`${prefix}IconColor`], '#2563eb')}
              onChange={(e) => setProp(`${prefix}IconColor`, e.target.value)}
            />
          </Field>
          <Field label="Taille (px)">
            <input
              type="number"
              min={16}
              max={48}
              className={inputClass}
              value={props[`${prefix}IconSize`] ?? 28}
              onChange={(e) => setProp(`${prefix}IconSize`, Number(e.target.value) || 28)}
            />
          </Field>
        </>
      ) : null}
      {mediaType === 'image' ? (
        <CardImageSourceField
          label="Image / SVG"
          urlKey={`${prefix}ImageUrl`}
          props={props}
          setProp={setProp}
          previewClassName="max-h-24"
        />
      ) : null}
    </div>
  );
}

export function CardFeatureContentFields({ props, setProp, siteTypography, sections = [] }) {
  return (
    <>
      <MediaCornerFields side="left" props={props} setProp={setProp} />
      <MediaCornerFields side="right" props={props} setProp={setProp} />
      <CardHtmlEditorField
        label="Titre"
        valueKey="title"
        formatKey="titleFormat"
        props={props}
        setProp={setProp}
        siteTypography={siteTypography}
      />
      <CardHtmlEditorField
        label="Description"
        valueKey="description"
        formatKey="descriptionFormat"
        props={props}
        setProp={setProp}
        siteTypography={siteTypography}
      />
      <CardHtmlEditorField
        label="Texte du lien"
        valueKey="linkLabel"
        formatKey="linkLabelFormat"
        props={props}
        setProp={setProp}
        siteTypography={siteTypography}
      />
      <div className="rounded-lg border border-border p-3 space-y-3 bg-muted/10">
        <p className="text-xs font-semibold text-foreground">Lien « En savoir plus »</p>
        <CardLinkFields props={props} setProp={setProp} sections={sections} />
      </div>
    </>
  );
}

export function CardFeatureAppearanceFields({ props, setProp }) {
  return (
    <>
      <ColorFillField
        label="Couleur du titre"
        value={props.titleColor}
        onChange={(fill) => setProp('titleColor', fill)}
        fallback="#0f172a"
      />
      <ColorFillField
        label="Couleur de la description"
        value={props.descriptionColor}
        onChange={(fill) => setProp('descriptionColor', fill)}
        fallback="#64748b"
      />
      <ColorFillField
        label="Couleur du lien"
        value={props.linkColor}
        onChange={(fill) => setProp('linkColor', fill)}
        fallback="#2563eb"
      />
      <ColorFillField
        label="Couleur du lien au survol"
        value={props.linkHoverColor}
        onChange={(fill) => setProp('linkHoverColor', fill)}
        fallback="#1d4ed8"
      />
      <HoverEffectFields
        props={props}
        setProp={setProp}
        prefix="link"
        title="Effet au survol du lien"
        hint="Soulignement, surbrillance, etc. sur le lien « En savoir plus »."
      />
      <Field label="Taille titre (px)">
        <input
          type="number"
          min={14}
          max={32}
          className={inputClass}
          value={props.titleFontSize ?? 18}
          onChange={(e) => setProp('titleFontSize', Number(e.target.value) || 18)}
        />
      </Field>
    </>
  );
}

export function CardIllustrationContentFields({ props, setProp, siteTypography }) {
  return (
    <>
      <CardImageSourceField
        label="Image illustration"
        urlKey="imageUrl"
        altKey="imageAlt"
        props={props}
        setProp={setProp}
        showAlt
        previewClassName="max-h-48"
      />
      <CardHtmlEditorField
        label="Titre"
        valueKey="title"
        formatKey="titleFormat"
        props={props}
        setProp={setProp}
        siteTypography={siteTypography}
      />
      <CardHtmlEditorField
        label="Description"
        valueKey="description"
        formatKey="descriptionFormat"
        props={props}
        setProp={setProp}
        siteTypography={siteTypography}
      />
    </>
  );
}

export function CardIllustrationAppearanceFields({ props, setProp }) {
  return (
    <>
      <ColorFillField
        label="Couleur du titre"
        value={props.titleColor}
        onChange={(fill) => setProp('titleColor', fill)}
        fallback="#0f172a"
      />
      <ColorFillField
        label="Couleur de la description"
        value={props.descriptionColor}
        onChange={(fill) => setProp('descriptionColor', fill)}
        fallback="#64748b"
      />
      <Field label="Cadrage image">
        <select
          className={inputClass}
          value={props.imageObjectFit || 'cover'}
          onChange={(e) => setProp('imageObjectFit', e.target.value)}
        >
          <option value="cover">Remplir (cover)</option>
          <option value="contain">Contenir (contain)</option>
          <option value="fill">Étirer (fill)</option>
        </select>
      </Field>
      <HoverEffectFields
        props={props}
        setProp={setProp}
        prefix="image"
        title="Effet au survol de l’illustration"
        hint="Zoom, ombre, etc. sur la zone image."
      />
    </>
  );
}

export function CardSplitContentFields({ props, setProp, siteTypography, sections = [] }) {
  return (
    <>
      <CardHtmlEditorField
        label="Sur-titre (petit)"
        valueKey="eyebrow"
        formatKey="eyebrowFormat"
        props={props}
        setProp={setProp}
        siteTypography={siteTypography}
      />
      <CardHtmlEditorField
        label="Sous-titre"
        valueKey="subtitle"
        formatKey="subtitleFormat"
        props={props}
        setProp={setProp}
        siteTypography={siteTypography}
      />
      <CardHtmlEditorField
        label="Titre principal"
        valueKey="heading"
        formatKey="headingFormat"
        props={props}
        setProp={setProp}
        siteTypography={siteTypography}
      />
      <CardHtmlEditorField
        label="Description"
        valueKey="description"
        formatKey="descriptionFormat"
        props={props}
        setProp={setProp}
        siteTypography={siteTypography}
      />
      <CardImageSourceField
        label="Image (colonne droite)"
        urlKey="imageUrl"
        altKey="imageAlt"
        props={props}
        setProp={setProp}
        showAlt
        previewClassName="max-h-48"
      />
      <div className="rounded-lg border border-border p-3 space-y-3 bg-muted/10">
        <p className="text-xs font-semibold text-foreground">Bouton sous la description</p>
        <CardHtmlEditorField
          label="Libellé du bouton"
          valueKey="buttonLabel"
          formatKey="buttonLabelFormat"
          props={props}
          setProp={setProp}
          siteTypography={siteTypography}
        />
        <CardLinkFields
          props={props}
          setProp={setProp}
          prefix="button"
          sections={sections}
        />
      </div>
    </>
  );
}

export function CardSplitAppearanceFields({ props, setProp, siteTypography }) {
  return (
    <>
      <ColorFillField
        label="Couleur sur-titre"
        value={props.eyebrowColor}
        onChange={(fill) => setProp('eyebrowColor', fill)}
        fallback="#64748b"
      />
      <ColorFillField
        label="Couleur sous-titre"
        value={props.subtitleColor}
        onChange={(fill) => setProp('subtitleColor', fill)}
        fallback="#475569"
      />
      <ColorFillField
        label="Couleur titre principal"
        value={props.headingColor}
        onChange={(fill) => setProp('headingColor', fill)}
        fallback="#0f172a"
      />
      <ColorFillField
        label="Couleur description"
        value={props.descriptionColor}
        onChange={(fill) => setProp('descriptionColor', fill)}
        fallback="#64748b"
      />
      <ColorFillField
        label="Fond du bouton"
        value={props.buttonBackgroundColor}
        onChange={(fill) => setProp('buttonBackgroundColor', fill)}
        fallback="#2563eb"
      />
      <ColorFillField
        label="Texte du bouton"
        value={props.buttonTextColor}
        onChange={(fill) => setProp('buttonTextColor', fill)}
        fallback="#ffffff"
      />
      <ShapeFields
        shape={props.buttonShape}
        borderRadius={props.buttonBorderRadius}
        onChangeShape={(e) => setProp('buttonShape', e.target.value)}
        onChangeRadius={(e) => setProp('buttonBorderRadius', Number(e.target.value))}
        previewLabel="Bouton"
        preview={(radius) => (
          <span
            className="inline-block px-4 py-2 text-sm font-medium text-white"
            style={{
              borderRadius: `${radius}px`,
              backgroundColor: '#2563eb',
            }}
          >
            {props.buttonLabel || 'Action'}
          </span>
        )}
      />
      <ButtonTypographyFields
        props={{
          inheritSiteButtonFont: props.inheritSiteButtonFont,
          buttonFontFamily: props.buttonFontFamily,
        }}
        setProp={setProp}
        siteTypography={siteTypography}
      />
      <HoverEffectFields
        props={props}
        setProp={setProp}
        prefix="button"
        title="Effet au survol du bouton"
        hint="Identique aux boutons classiques (élévation, zoom…)."
      />
      <HoverEffectFields
        props={props}
        setProp={setProp}
        prefix="image"
        title="Effet au survol de l’image"
        hint="Sur la colonne image à droite."
      />
    </>
  );
}

export function CardSharedAppearanceFields({ props, setProp }) {
  return (
    <>
      <HoverEffectFields
        props={props}
        setProp={setProp}
        prefix="card"
        title="Effet au survol de la carte"
        hint="S’applique à toute la carte (ombre, élévation…)."
      />
      <ShapeFields
      shape={props.shape}
      borderRadius={props.borderRadius}
      onChangeShape={(e) => setProp('shape', e.target.value)}
      onChangeRadius={(e) => setProp('borderRadius', Number(e.target.value))}
      previewLabel="Carte"
      preview={(radius) => (
        <div
          className="w-full max-w-[160px] h-20 bg-muted border border-border"
          style={{ borderRadius: `${radius}px` }}
        />
      )}
    />
    </>
  );
}
